import axios from 'axios';

const OPENAI_API_URL = 'https://api.openai.com/v1/chat/completions';
const OPENAI_API_KEY = process.env.REACT_APP_OPENAI_API_KEY;

const OPENAI_IMAGE_API_URL = 'https://api.openai.com/v1/images/generations';

const MAX_RETRIES = 5;
const INITIAL_RETRY_DELAY = 1000; // 1 second

const axiosInstance = axios.create({
  baseURL: OPENAI_API_URL,
  headers: {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${OPENAI_API_KEY}`,
  },
});

const exponentialBackoff = (retryCount) => {
  return Math.pow(2, retryCount) * INITIAL_RETRY_DELAY;
};

export const generatePostContent = async (topic, summary) => {
  let retryCount = 0;

  while (retryCount < MAX_RETRIES) {
    try {
      const response = await axiosInstance.post('', {
        model: 'gpt-3.5-turbo',
        messages: [
          {
            role: 'system',
            content:
              'You are a helpful assistant that generates blog post content.',
          },
          {
            role: 'user',
            content: `Generate a blog post about "${topic}". Here's a summary of what I want: "${summary}". Please provide a well-structured post with an introduction, main points, and a conclusion.`,
          },
        ],
        max_tokens: 500,
      });

      return response.data.choices[0].message.content.trim();
    } catch (error) {
      if (error.response && error.response.status === 429) {
        retryCount++;
        const delay = exponentialBackoff(retryCount);
        console.log(`Rate limited. Retrying in ${delay / 1000} seconds...`);
        await new Promise((resolve) => setTimeout(resolve, delay));
      } else {
        console.error('Error generating content with OpenAI:', error);
        throw new Error('Failed to generate content');
      }
    }
  }

  throw new Error('Max retries reached. Failed to generate content.');
};

export const generateImage = async (
  title,
  description,
  referenceImageUrl = null,
) => {
  let retryCount = 0;

  while (retryCount < MAX_RETRIES) {
    try {
      // First, use GPT-4 to create an optimized image prompt
      const promptResponse = await axiosInstance.post('', {
        model: 'gpt-4',
        messages: [
          {
            role: 'system',
            content: `You are an expert at creating detailed image generation prompts. You specialize in creating prompts that generate professional, attractive marketing banner images for services.`,
          },
          {
            role: 'user',
            content: `Create a detailed DALL-E prompt for a service banner image. The service is: "${title}". 
            Description: "${description}".
            
            Requirements:
            - Must be a landscape-oriented banner style
            - Should look professional and attractive to potential buyers
            - Include relevant visual elements that represent the service
            - Use modern, clean design principles
            - Avoid text in the image
            - Include specific details about lighting, composition, and style
            - Make it suitable for a marketplace listing banner
            
            Format the prompt to be direct and specific.`,
          },
        ],
        max_tokens: 500,
        temperature: 0.7,
      });

      const optimizedPrompt =
        promptResponse.data.choices[0].message.content.trim();

      // Prepare the image generation request
      const imageRequest = {
        model: 'dall-e-3', // Specify DALL-E 3 model
        prompt: optimizedPrompt,
        n: 1,
        size: '1792x1024', // Use landscape aspect ratio
        quality: 'standard',
        response_format: 'b64_json',
      };

      // If we have a reference image, add it to the request
      if (referenceImageUrl) {
        imageRequest.prompt += ` The style and layout should be similar to the reference image provided.`;
      }

      // Use the optimized prompt to generate the image
      const response = await axios.post(OPENAI_IMAGE_API_URL, imageRequest, {
        headers: {
          Authorization: `Bearer ${OPENAI_API_KEY}`,
          'Content-Type': 'application/json',
        },
      });

      // Log the prompt for debugging (optional)
      console.log('Generated prompt:', optimizedPrompt);

      return response.data.data[0].b64_json;
    } catch (error) {
      if (error.response?.status === 429) {
        retryCount++;
        const delay = exponentialBackoff(retryCount);
        console.log(`Rate limited. Retrying in ${delay / 1000} seconds...`);
        await new Promise((resolve) => setTimeout(resolve, delay));
      } else {
        console.error('Error details:', error.response?.data);
        throw new Error(
          error.response?.data?.error?.message || 'Failed to generate image',
        );
      }
    }
  }

  throw new Error('Max retries reached. Failed to generate image.');
};
